import React, {Fragment, useCallback, useEffect, useReducer} from "react";
import AsyncSelect from "react-select/async";
import {Datepicker} from "@mobiscroll/react";
import {useAuth0} from "@auth0/auth0-react";

import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";

import {LoadingButton} from "../shared/loading-button";
import ai from "../../helpers/axios";
import Loader from "../loader";

export const FiltersTargets = {
  Shared: 'shared',
  OFR: 'ofr',
  Contract: 'contract',
}

const dropdowns = {
  isoCountry: {name: "isoCountry", showDefaultOptions: true, placeholder: 'Country'},
  region: {name: "region", showDefaultOptions: true, placeholder: 'Region'},
  subRegion: {name: "subRegion", showDefaultOptions: true, placeholder: 'Sub-region'},
  isoRegion: {name: "isoRegion", showDefaultOptions: true, placeholder: 'State'},
  municipality: {name: "municipality", showDefaultOptions: false, placeholder: 'Municipality'},
};

const FiltersLabels = {
  [FiltersTargets.Shared]: 'OFRs/Contracts Filters',
  [FiltersTargets.OFR]: 'OFRs Filters',
  [FiltersTargets.Contract]: 'Contracts Filters',
}

export const FiltersSections = {
  Dates: 'dates',
  Departure: 'departure',
  Arrival: 'arrival',
}

const initialFilters = {
  [FiltersSections.Dates]: [],
  [FiltersSections.Departure]: {
    ...Object.fromEntries(Object.keys(dropdowns).map(key => [key, []])),
    airport: [],
  },
  [FiltersSections.Arrival]: {
    ...Object.fromEntries(Object.keys(dropdowns).map(key => [key, []])),
    airport: [],
  },
};

const initialFiltersState = {
  prefillLoading: true,
  filters: {
    areShared: true,
    [FiltersTargets.Shared]: initialFilters,
    [FiltersTargets.OFR]: initialFilters,
    [FiltersTargets.Contract]: initialFilters,
  },
  defaultOptions: Object.keys(dropdowns)
    .reduce((res, field) => ({...res, [field]: []}), {}),
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "prefillDefaultOptions": {
      return {
        ...state,
        defaultOptions: Object.entries(action.value).reduce((res, [field, options]) => ({
          ...res,
          [field]: options.map(option => ({value: option, label: option})),
        }), []),
        prefillLoading: false,
      }
    }

    default:
      break;
  }
}

export const MetricsUtilizationFilters = ({applyFilters}) => {
    const {getAccessTokenSilently, loginWithRedirect} = useAuth0();

    const [state, dispatch] = useReducer(reducer, initialFiltersState);

    const loadPrefillData = useCallback(async () => {
      const token = await getAccessTokenSilently().catch((e) => {
        return loginWithRedirect();
      });

      try {
        let {data} = await ai.auth(token).get(`/api/airports/metrics-filters`, {
          params: {
            field: 'all',
          },
        });

        if (data) {
          dispatch({
            type: 'prefillDefaultOptions',
            value: data,
          });
        }
      } catch (e) {
        console.error(e);
        dispatch({
          type: 'fieldChanged',
          field: 'prefillLoading',
          value: false,
        });
      }
    }, [getAccessTokenSilently, loginWithRedirect]);

    useEffect(() => {
      void loadPrefillData();
    }, [loadPrefillData])

    const loadDropdownOptions = useCallback((field) => async (inputValue) => {
      const token = await getAccessTokenSilently().catch((e) => {
        return loginWithRedirect();
      });

      let {data} = await ai.auth(token).get(`/api/airports/metrics-filters`, {
        params: {
          field,
          search: inputValue,
        },
      });

      if (data?.[field]?.length >= 1) {
        return data[field].map((option) => {
          return {value: option, label: option};
        });
      } else {
        return [];
      }
    }, [getAccessTokenSilently, loginWithRedirect]);

    const loadAirportOptions = useCallback(async (inputValue) => {
      const token = await getAccessTokenSilently().catch((e) => {
        return loginWithRedirect();
      });

      let response = await ai
        .auth(token)
        .get(`/api/airports/search?q=${inputValue}`)
        .catch((error) => {
          console.error(error);
        });

      let options = [];
      if (response.data.length >= 1) {
        options = response.data.map((i) => ({
          value: i._id,
          label: i.airportName,
          code: i.code,
        }));
      }

      return options;
    }, [getAccessTokenSilently, loginWithRedirect]);

    const handleUpdateFilters = useCallback(({target, section, field, value}) => {
      const isDatesRangeChange = section === FiltersSections.Dates;

      const updatedFilters = {
        ...state.filters,
        [target]: {
          ...state.filters[target],
          ...(isDatesRangeChange ? {
            [FiltersSections.Dates]: value,
          } : {
            [section]: {
              ...state.filters[target][section],
              [field]: value,
            }
          })
        }
      };

      dispatch({
        type: "fieldChanged",
        field: 'filters',
        value: updatedFilters,
      });
    }, [state.filters]);

    const handleFiltersAreSharedChange = useCallback((areShared) => dispatch({
      type: "fieldChanged",
      field: "filters",
      value: {
        ...initialFiltersState.filters,
        ...(areShared ? {
          [FiltersTargets.Shared]: state.filters[FiltersTargets.OFR],
        } : {
          [FiltersTargets.OFR]: state.filters[FiltersTargets.Shared],
          [FiltersTargets.Contract]: state.filters[FiltersTargets.Shared],
        }),
        areShared,
      }
    }), [state.filters]);

    const displayTargets = state.filters.areShared ? [FiltersTargets.Shared] : [FiltersTargets.OFR, FiltersTargets.Contract];

    return <Fragment>
      {state.prefillLoading ?
        <Box height={335} display="flex" alignItems="center">
          <Loader/>
        </Box>
        : <>
          <div className="checkboxContainer ml-0 mb-20">
            <label htmlFor="name">Same for OFRs and Contracts</label>
            <input
              type="checkbox"
              name="active"
              checked={state.filters.areShared}
              onChange={(e) =>
                handleFiltersAreSharedChange(e.target.checked)}
            />
          </div>

          {displayTargets.map((target, index) =>
            <>
              <Box display="flex" justifyContent="space-between" mt={2}>
                <h5>{FiltersLabels[target]}</h5>
                <LoadingButton className="base-button evo-blue"
                               onClick={() => applyFilters({target, filters: state.filters})}>
                  Apply&nbsp;{target !== FiltersTargets.Shared ? FiltersLabels[target] : ''}
                </LoadingButton>
              </Box>

              <Stack direction="row" mt={1} gap={2} className="metrics-utilization-filters">
                <Stack pr={2} flex={1}>
                  <h6>DATES</h6>
                  <Datepicker
                    select="range"
                    returnFormat="iso8601"
                    controls={['date']}
                    value={state.filters[target].dates}
                    placeholder="__/__/____ - __/__/____"
                    onChange={(range) => handleUpdateFilters({
                      target,
                      section: FiltersSections.Dates,
                      field: FiltersSections.Dates,
                      value: range.value,
                    })}
                    themeVariant="light"
                    inputProps={{
                      readOnly: true,
                      className: "dates-range-light-input"
                    }}
                  />
                </Stack>

                {[
                  FiltersSections.Departure,
                  FiltersSections.Arrival
                ].map(section =>
                  <Stack flex={2}>
                    <h6>{section === FiltersSections.Departure ? 'DEPARTURE' : 'ARRIVAL'} FILTERS</h6>

                    <div className="fields-container">
                      {Object.entries(dropdowns).map(([field, {placeholder}]) =>
                        <AsyncSelect
                          className="base-select"
                          classNamePrefix="select"
                          cacheOptions
                          isMulti
                          loadOptions={loadDropdownOptions(field)}
                          placeholder={placeholder}
                          onChange={(value) => handleUpdateFilters({target, section, field, value})}
                          value={state.filters[target][section][field] ?? []}
                          defaultOptions={dropdowns[field].showDefaultOptions ? state.defaultOptions[field] : []}
                          noOptionsMessage={() => !dropdowns[field].showDefaultOptions ? "Type to search..." : "No options"}
                        />)
                      }

                      <AsyncSelect
                        className="base-select"
                        classNamePrefix="select"
                        cacheOptions
                        isMulti
                        loadOptions={loadAirportOptions}
                        placeholder="Airport"
                        onChange={(value) => handleUpdateFilters({target, section, field: "airport", value})}
                        value={state.filters[target][section].airport ?? []}
                        noOptionsMessage={() => "Type to search..."}
                      />
                    </div>
                  </Stack>
                )}
              </Stack>

              {index === 0 && displayTargets.length > 1 && <Divider sx={{mt: 3}}/>}
            </>
          )}
        </>
      }
    </Fragment>;
  }
;